import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Upload, 
  Download, 
  FileText, 
  AlertCircle, 
  CheckCircle,
  X,
  Loader2
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../../hooks/useToast';
import { useTenantContext } from '../../../context/TenantContext';
import { addQuestionToQuiz, addAnswerToQuestion } from '../../../services/elearningQuizService';
import { Quiz, QuizQuestion } from '../../../services/elearningQuizService';

interface QuestionImportExportProps {
  quiz: Quiz;
  onQuestionsImported: (questions: QuizQuestion[]) => void;
}

interface ImportedQuestion {
  question_text: string;
  question_type: 'multiple_choice' | 'single_choice' | 'true_false' | 'text_input' | 'matching' | 'ordering';
  points: number;
  explanation?: string;
  is_required: boolean;
  answers: {
    answer_text: string;
    is_correct: boolean;
    explanation?: string;
  }[];
}

const QuestionImportExport: React.FC<QuestionImportExportProps> = ({ quiz, onQuestionsImported }) => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [importData, setImportData] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importPreview, setImportPreview] = useState<ImportedQuestion[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  // Export des questions au format JSON
  const exportQuestionsJSON = () => {
    if (!quiz.questions || quiz.questions.length === 0) {
      toast({
        title: 'Aucune question',
        description: 'Ce quiz ne contient aucune question à exporter.',
        variant: 'destructive'
      });
      return;
    }

    const exportData = quiz.questions.map(question => ({
      question_text: question.question_text,
      question_type: question.question_type,
      points: question.points,
      explanation: question.explanation || '',
      is_required: question.is_required,
      answers: (question.answers || []).map(answer => ({
        answer_text: answer.answer_text,
        is_correct: answer.is_correct,
        explanation: answer.explanation || ''
      }))
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quiz-${quiz.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-questions.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: 'Export réussi',
      description: `${quiz.questions.length} question(s) exportée(s) au format JSON.`
    });
  };

  // Export des questions au format CSV
  const exportQuestionsCSV = () => {
    if (!quiz.questions || quiz.questions.length === 0) {
      toast({
        title: 'Aucune question',
        description: 'Ce quiz ne contient aucune question à exporter.',
        variant: 'destructive'
      });
      return;
    }

    const csvHeaders = [
      'Question',
      'Type',
      'Points',
      'Explication',
      'Obligatoire',
      'Réponse 1',
      'Correcte 1',
      'Réponse 2',
      'Correcte 2',
      'Réponse 3',
      'Correcte 3',
      'Réponse 4',
      'Correcte 4'
    ];

    const csvRows = quiz.questions.map(question => {
      const answers = question.answers || [];
      const row = [
        `"${question.question_text.replace(/"/g, '""')}"`,
        question.question_type,
        question.points.toString(),
        `"${(question.explanation || '').replace(/"/g, '""')}"`,
        question.is_required ? 'Oui' : 'Non'
      ];

      // Ajouter jusqu'à 4 réponses
      for (let i = 0; i < 4; i++) {
        if (i < answers.length) {
          row.push(`"${answers[i].answer_text.replace(/"/g, '""')}"`);
          row.push(answers[i].is_correct ? 'Oui' : 'Non');
        } else {
          row.push('');
          row.push('');
        }
      }

      return row.join(',');
    });

    const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quiz-${quiz.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-questions.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: 'Export réussi',
      description: `${quiz.questions.length} question(s) exportée(s) au format CSV.`
    });
  };

  // Validation des données importées
  const validateImportData = (data: any[]): { questions: ImportedQuestion[], errors: string[] } => {
    const questions: ImportedQuestion[] = [];
    const errors: string[] = [];

    data.forEach((item, index) => {
      const questionIndex = index + 1;

      // Validation des champs obligatoires
      if (!item.question_text || typeof item.question_text !== 'string') {
        errors.push(`Question ${questionIndex}: Texte de la question manquant ou invalide`);
        return;
      }

      if (!item.question_type || !['multiple_choice', 'single_choice', 'true_false', 'text_input', 'matching', 'ordering'].includes(item.question_type)) {
        errors.push(`Question ${questionIndex}: Type de question invalide`);
        return;
      }

      if (!item.points || typeof item.points !== 'number' || item.points <= 0) {
        errors.push(`Question ${questionIndex}: Points invalides`);
        return;
      }

      // Validation des réponses
      if (!Array.isArray(item.answers) || item.answers.length === 0) {
        errors.push(`Question ${questionIndex}: Aucune réponse fournie`);
        return;
      }

      const validAnswers = item.answers.filter((answer: any) => 
        answer.answer_text && typeof answer.answer_text === 'string'
      );

      if (validAnswers.length === 0) {
        errors.push(`Question ${questionIndex}: Aucune réponse valide`);
        return;
      }

      // Vérifier qu'il y a au moins une bonne réponse pour les questions à choix
      if (['multiple_choice', 'single_choice', 'true_false'].includes(item.question_type)) {
        const correctAnswers = validAnswers.filter((answer: any) => answer.is_correct === true);
        if (correctAnswers.length === 0) {
          errors.push(`Question ${questionIndex}: Aucune bonne réponse définie`);
          return;
        }
      }

      questions.push({
        question_text: item.question_text,
        question_type: item.question_type,
        points: item.points,
        explanation: item.explanation || '',
        is_required: item.is_required === true,
        answers: validAnswers.map((answer: any) => ({
          answer_text: answer.answer_text,
          is_correct: answer.is_correct === true,
          explanation: answer.explanation || ''
        }))
      });
    });

    return { questions, errors };
  };

  // Prévisualisation des données d'import
  const previewImportData = () => {
    if (!importData.trim()) {
      setImportErrors(['Aucune donnée à importer']);
      return;
    }

    try {
      const parsedData = JSON.parse(importData);
      if (!Array.isArray(parsedData)) {
        setImportErrors(['Le format JSON doit être un tableau de questions']);
        return;
      }

      const { questions, errors } = validateImportData(parsedData);
      setImportPreview(questions);
      setImportErrors(errors);
    } catch (error) {
      setImportErrors(['Format JSON invalide']);
    }
  };

  // Import des questions
  const importQuestions = async () => {
    if (!tenant?.id || importPreview.length === 0) return;

    setIsImporting(true);
    try {
      const importedQuestions: QuizQuestion[] = [];

      for (const questionData of importPreview) {
        // Créer la question
        const newQuestion = await addQuestionToQuiz(quiz.id, {
          question_text: questionData.question_text,
          question_type: questionData.question_type,
          points: questionData.points,
          explanation: questionData.explanation,
          is_required: questionData.is_required
        }, tenant.id);

        // Ajouter les réponses
        const answers = [];
        for (const answerData of questionData.answers) {
          const newAnswer = await addAnswerToQuestion(newQuestion.id, {
            answer_text: answerData.answer_text,
            is_correct: answerData.is_correct,
            explanation: answerData.explanation
          }, tenant.id);
          answers.push(newAnswer);
        }

        importedQuestions.push({ ...newQuestion, answers });
      }

      onQuestionsImported(importedQuestions);
      setIsImportDialogOpen(false);
      setImportData('');
      setImportPreview([]);
      setImportErrors([]);

      toast({
        title: 'Import réussi',
        description: `${importedQuestions.length} question(s) importée(s) avec succès.`
      });
    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
      toast({
        title: 'Erreur d\'import',
        description: 'Une erreur est survenue lors de l\'import des questions.',
        variant: 'destructive'
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Import depuis fichier
  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setImportData(content);
      setIsImportDialogOpen(true);
    };
    reader.readAsText(file);
  };

  return (
    <>
      <div className="flex space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsExportDialogOpen(true)}
          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
        >
          <Download size={16} className="mr-2" />
          Exporter
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsImportDialogOpen(true)}
          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
        >
          <Upload size={16} className="mr-2" />
          Importer
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileImport}
          className="hidden"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
        >
          <FileText size={16} className="mr-2" />
          Fichier
        </Button>
      </div>

      {/* Dialog Export */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent className="bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Exporter les questions</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-400">
              Choisissez le format d'export pour les {quiz.questions?.length || 0} questions de ce quiz.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={exportQuestionsJSON}
                className="bg-green-600 hover:bg-green-700 text-white h-20 flex-col"
              >
                <FileText size={24} className="mb-2" />
                Format JSON
                <span className="text-xs opacity-70">Recommandé pour la réimportation</span>
              </Button>
              <Button
                onClick={exportQuestionsCSV}
                className="bg-blue-600 hover:bg-blue-700 text-white h-20 flex-col"
              >
                <Download size={24} className="mb-2" />
                Format CSV
                <span className="text-xs opacity-70">Compatible Excel</span>
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExportDialogOpen(false)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Import */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="bg-black/90 border-white/10 text-white max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Importer des questions</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto max-h-[70vh]">
            <div>
              <label className="text-sm font-medium text-gray-300 mb-2 block">
                Données JSON des questions
              </label>
              <Textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="Collez ici le JSON des questions à importer..."
                className="bg-gray-700 border-gray-600 text-white h-32"
              />
              <div className="flex justify-between items-center mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={previewImportData}
                  className="border-blue-500/30 text-blue-400"
                >
                  Prévisualiser
                </Button>
                <span className="text-xs text-gray-400">
                  Format JSON attendu : tableau d'objets question
                </span>
              </div>
            </div>

            {/* Erreurs d'import */}
            {importErrors.length > 0 && (
              <Card className="bg-red-500/10 border-red-500/30">
                <CardHeader>
                  <CardTitle className="text-red-400 flex items-center text-sm">
                    <AlertCircle size={16} className="mr-2" />
                    Erreurs détectées ({importErrors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {importErrors.map((error, index) => (
                      <li key={index} className="text-red-300 text-xs">• {error}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Prévisualisation des questions */}
            {importPreview.length > 0 && (
              <Card className="bg-green-500/10 border-green-500/30">
                <CardHeader>
                  <CardTitle className="text-green-400 flex items-center text-sm">
                    <CheckCircle size={16} className="mr-2" />
                    Questions à importer ({importPreview.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-60 overflow-y-auto">
                  <div className="space-y-3">
                    {importPreview.map((question, index) => (
                      <div key={index} className="bg-gray-700/30 p-3 rounded border border-gray-600/30">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 bg-cyan-500/10 text-xs">
                              Q{index + 1}
                            </Badge>
                            <Badge variant="outline" className="border-purple-500/30 text-purple-400 bg-purple-500/10 text-xs">
                              {question.question_type}
                            </Badge>
                            <Badge variant="outline" className="border-yellow-500/30 text-yellow-400 bg-yellow-500/10 text-xs">
                              {question.points} pts
                            </Badge>
                          </div>
                        </div>
                        <p className="text-white text-sm mb-2">
                          {question.question_text.length > 100 
                            ? `${question.question_text.substring(0, 100)}...` 
                            : question.question_text}
                        </p>
                        <div className="text-xs text-gray-400">
                          {question.answers.length} réponse(s) • 
                          {question.answers.filter(a => a.is_correct).length} correcte(s)
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={importQuestions}
              disabled={importPreview.length === 0 || importErrors.length > 0 || isImporting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isImporting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Import en cours...
                </>
              ) : (
                `Importer ${importPreview.length} question(s)`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { QuestionImportExport };
